import { useState } from 'react';
import { motion } from 'framer-motion';
import { EcwidLink } from '@/components/EcwidWidgets';
import { ArrowRight, Eye } from 'lucide-react';
import QuickView from '@/components/QuickView';

export function Price({ product, className = '' }) {
  const { price, minPrice, maxPrice, regularPrice, onSale, currency = '$' } = product;
  if (minPrice != null && maxPrice != null && minPrice !== maxPrice) {
    return <span className={className}>{currency}{minPrice.toFixed(2)} – {currency}{maxPrice.toFixed(2)}</span>;
  }
  if (onSale && regularPrice && price != null && price < regularPrice) {
    return (
      <span className={className}>
        <span className="mr-2 text-ash line-through">{currency}{regularPrice.toFixed(2)}</span>
        {currency}{price.toFixed(2)}
      </span>
    );
  }
  return <span className={className}>{price != null ? `${currency}${price.toFixed(2)}` : 'See options'}</span>;
}

export default function ProductCard({ product, index = 0 }) {
  const [quickView, setQuickView] = useState(false);
  const category = product.categories?.[0];
  const buyUrl = `/shop#!/~/product/id=${product.id}`;

  return (
    <>
      <motion.article
        data-testid={`product-card-${product.slug}`}
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-40px' }}
        transition={{ duration: 0.6, delay: (index % 4) * 0.08 }}
        className="group relative flex flex-col border border-ink/10 bg-surface transition-colors duration-300 hover:border-gold/40"
      >
        <div className="relative aspect-square overflow-hidden bg-bone">
          {product.image ? (
            <img
              src={product.image}
              alt={product.imageAlt || product.name}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
          ) : (
            <div className="flex h-full items-center justify-center font-serif text-4xl text-forest">MG</div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
          {product.onSale && (
            <span data-testid={`sale-badge-${product.slug}`} className="absolute left-3 top-3 bg-gold px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-ink">
              Deal
            </span>
          )}
          {!product.inStock && (
            <span className="absolute right-3 top-3 bg-oxblood px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-ink">
              Sold Out
            </span>
          )}
          <button
            data-testid={`quick-view-${product.slug}`}
            onClick={() => setQuickView(true)}
            className="absolute bottom-3 right-3 flex items-center gap-1.5 border border-ink/20 bg-bone/70 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-ink opacity-0 backdrop-blur-md transition-all duration-300 hover:border-gold hover:text-bronze group-hover:opacity-100"
          >
            <Eye className="h-3.5 w-3.5" /> Quick View
          </button>
        </div>

        <div className="flex flex-1 flex-col p-5">
          {category && (
            <span className="text-[10px] font-semibold uppercase tracking-[0.25em] text-moss">{category.name.replace(/^Buy | Online$/g, '')}</span>
          )}
          <h3 className="mt-1.5 font-serif text-xl leading-snug text-ink">{product.name}</h3>
          <div className="mt-2 text-xs font-semibold text-bronze">
            <Price product={product} />
          </div>

          <div className="mt-auto pt-4">
            <EcwidLink
              to={buyUrl}
              data-testid={`buy-${product.slug}`}
              className="flex w-full items-center justify-center gap-2 border border-gold/60 py-3 text-[11px] font-bold uppercase tracking-[0.2em] text-bronze transition-all duration-300 hover:bg-gold hover:text-ink"
            >
              {product.inStock ? 'Choose Options' : 'Sold Out'} <ArrowRight className="h-4 w-4" />
            </EcwidLink>
          </div>
        </div>
      </motion.article>

      {quickView && <QuickView slug={product.slug} onClose={() => setQuickView(false)} />}
    </>
  );
}
